import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Modal } from "components/designV2/modal/modal";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { Text } from "components/designV2/text/text";
import { CircularProgress } from "@material-ui/core";
import { ColorV2 } from "config/colors";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { LocalDate } from "@js-joda/core";
import { SalaryNotice } from "model/Salary";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { isAdjustableMonth } from "web-apps/company/utils/timeAndDate";
import {
  SalaryPageBottomMargin,
  PeriodNavigatorContainer,
  SalaryPeriodCard,
  TooltipContainer,
} from "../salary.styled";
import { Pagination } from "../../general/pagination.component";
import { FileViewer } from "../../fileViewer/fileViewer.component";
import { ModalState } from "../individualSalary.component";
import { AdjustSalaryModal } from "../adjustSalaryModal.component";
import {
  useMonthlySalary,
  type MonthlySalaryLoadedState,
} from "./monthlySalary.hook";
import { MonthlySalaryRow } from "./monthlySalaryRow.component";
import { EmptyStateText, SalaryList } from "./monthlySalary.styled";

interface AdjustModalState {
  open: boolean;
  salaryNoticeId?: number;
  workerId?: number;
  workerName?: string;
}

interface MonthlySalaryProps {
  currentDate: LocalDate;
  onOpenGenerateSalaryNoticesModal: () => void;
  refreshKey: number;
  setCurrentDate: (date: LocalDate) => void;
}

const assertNever = (value: never): never => {
  throw new Error(`Unhandled monthly salary state: ${JSON.stringify(value)}`);
};

export const MonthlySalary = (props: MonthlySalaryProps): JSX.Element => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages, workerIdToExternalId }] =
    useCompanyStore();
  const { fetchState, page, setPage, getSalaries } = useMonthlySalary(
    company?.id,
    props.currentDate,
  );
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    workerId: undefined,
    pdfId: undefined,
    period: undefined,
    workerName: undefined,
  });
  const [adjustModalState, setAdjustModalState] = useState<AdjustModalState>({
    open: false,
  });

  const openSalaryPdf = useCallback((salary: SalaryNotice) => {
    setModalState({
      open: true,
      workerId: salary.workerId,
      pdfId: salary.id,
      period: salary.period,
      workerName: salary.workerName,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      workerId: undefined,
      pdfId: undefined,
      period: undefined,
      workerName: undefined,
    });
  }, []);

  const openAdjustModal = useCallback((salary: SalaryNotice) => {
    setAdjustModalState({
      open: true,
      salaryNoticeId: salary.id,
      workerId: salary.workerId,
      workerName: salary.workerName,
    });
  }, []);

  const closeAdjustModal = useCallback(() => {
    setAdjustModalState({ open: false });
  }, []);

  const handleAdjustSaved = useCallback(() => {
    closeAdjustModal();
    getSalaries();
  }, [closeAdjustModal, getSalaries]);

  const { open, workerId, pdfId, period, workerName } = modalState;

  useEffect(() => {
    if (props.refreshKey === 0) return;
    getSalaries();
  }, [getSalaries, props.refreshKey]);

  const canAdjustForDisplayedMonth = isAdjustableMonth(props.currentDate);

  const renderSalaryRows = (salaryNotices: SalaryNotice[]): JSX.Element[] => {
    return salaryNotices.map((salary) => {
      const currency = salary.currency === "SEK" ? " kr" : "";
      const externalId = workerIdToExternalId[salary.workerId];

      return (
        <MonthlySalaryRow
          key={`monthlysalary-${salary.id}`}
          salary={salary}
          avatarUrl={companyWorkerImages[externalId]}
          currency={currency}
          canAdjustForDisplayedMonth={canAdjustForDisplayedMonth}
          onOpenPdf={openSalaryPdf}
          onOpenAdjust={openAdjustModal}
        />
      );
    });
  };

  const renderLoadingState = (): JSX.Element => (
    <EmptyStateText>
      <CircularProgress />
    </EmptyStateText>
  );

  const renderErrorState = (): JSX.Element => (
    <EmptyStateText>
      <Text typography="b2" align="center" color={ColorV2.NegativeRed}>
        {t("Salary.MonthlySalaryLoadError")}
      </Text>
    </EmptyStateText>
  );

  const renderEmptyState = (): JSX.Element => (
    <EmptyStateText>
      <Text typography="b2" align="center">
        {t("Salary.GenerateSalaryNoticesEmptyState")}
      </Text>
      <PrimaryButton
        label={t("Salary.GenerateSalaryNotices")}
        iconName="v2/plus"
        onClick={props.onOpenGenerateSalaryNoticesModal}
        size="small"
      />
    </EmptyStateText>
  );

  const renderLoadedState = (state: MonthlySalaryLoadedState): JSX.Element => {
    if (state.salaryNotices.length === 0) {
      return renderEmptyState();
    }

    return (
      <>
        <SalaryList>{renderSalaryRows(state.salaryNotices)}</SalaryList>
        {state.totalPages > 1 && (
          <Pagination
            setPage={setPage}
            page={page}
            totalPages={state.totalPages}
          />
        )}
      </>
    );
  };

  const renderSalaryContent = (): JSX.Element => {
    switch (fetchState.status) {
      case "idle":
      case "loading":
        return renderLoadingState();
      case "error":
        return renderErrorState();
      case "loaded":
        return renderLoadedState(fetchState);
      default:
        return assertNever(fetchState);
    }
  };

  return (
    <SalaryPageBottomMargin>
      <TooltipContainer>
        <TooltipHover text={t("Salary.MonthlySalarySlipInfo")} arrow="up">
          <Text typography="s1">
            {t(`Salary.WhatIs`, {
              name: t("Salary.MonthlySalary").toLowerCase(),
            })}
          </Text>
        </TooltipHover>
      </TooltipContainer>

      <SalaryPeriodCard>
        <PeriodNavigatorContainer>
          <PeriodNavigator
            currentDate={props.currentDate}
            mode="month"
            onChange={props.setCurrentDate}
          />
        </PeriodNavigatorContainer>
      </SalaryPeriodCard>

      {renderSalaryContent()}

      {open && company && workerId && pdfId && period && workerName && (
        <Modal
          isOpen={open}
          onClose={closeModal}
          label={t("Salary.ViewSalaryNoticeTooltip")}
          size="large"
        >
          <FileViewer
            fetchEndpoint={() =>
              Api().company.salary.generateSalaryNotice(
                company.id,
                workerId,
                pdfId,
              )
            }
            filename={`SalaryNotice_${period}_${workerName}.pdf`}
            type="application/pdf"
          />
        </Modal>
      )}

      {company &&
        adjustModalState.open &&
        adjustModalState.salaryNoticeId &&
        adjustModalState.workerId &&
        adjustModalState.workerName && (
          <AdjustSalaryModal
            open={adjustModalState.open}
            companyId={company.id}
            salaryNoticeId={adjustModalState.salaryNoticeId}
            workerId={adjustModalState.workerId}
            workerName={adjustModalState.workerName}
            onClose={closeAdjustModal}
            onSaved={handleAdjustSaved}
          />
        )}
    </SalaryPageBottomMargin>
  );
};
